import { PuroReservation, Group } from '../models';

/**
 * Budget calculation utilities
 */

export interface BudgetInfo {
  percentage: number;
  amount: number;
  is40Percent: boolean;
  is60Percent: boolean;
  is100Percent: boolean;
}

/**
 * Calculate budget percentage based on guest type and V-Level
 * - Non-Valamar guest: 40%
 * - Valamar regular guest (pical): 60%
 * - V-Level guest: 100% (only for specific hotels)
 */
export function getBudgetPercentage(isValamarGuest: boolean, vLevel: boolean): number {
  if (!isValamarGuest) {
    return 40;
  }
  return vLevel ? 100 : 60;
}

/**
 * Calculate available budget for a guest
 */
export function calculateAvailableBudget(reservation: PuroReservation, percentage: number): number {
  return (reservation.dailyBudget * percentage) / 100;
}

/**
 * Calculate budget per chair
 */
export function calculateBudgetPerChair(reservation: PuroReservation, groups: Group[], percentage: number): number {
  const totalBudget = calculateAvailableBudget(reservation, percentage);
  const totalChairs = groups.reduce((sum, group) => sum + group.sunbeds.length, 0);
  return totalChairs > 0 ? totalBudget / totalChairs : 0;
}

/**
 * Get budget information object with flags
 */
export function getBudgetInfo(isValamarGuest: boolean, vLevel: boolean, dailyBudget: number): BudgetInfo {
  const percentage = getBudgetPercentage(isValamarGuest, vLevel);
  const amount = (dailyBudget * percentage) / 100;

  return {
    percentage,
    amount,
    is40Percent: percentage === 40,
    is60Percent: percentage === 60,
    is100Percent: percentage === 100
  };
}

/**
 * Calculate group budget based on number of chairs and per-chair budget
 */
export function calculateGroupBudget(chairCount: number, budgetPerChair: number): number {
  return Math.round(chairCount * budgetPerChair * 100) / 100;
}

/**
 * Validate if total group budgets exceed available budget
 */
export function validateBudgets(reservation: PuroReservation, groups: Group[], percentage: number): {
  isValid: boolean;
  availableBudget: number;
  totalBudgetUsed: number;
  overdraft: number;
} {
  const availableBudget = calculateAvailableBudget(reservation, percentage);
  const totalBudgetUsed = groups.reduce((sum, group) => sum + group.price, 0);
  const overdraft = totalBudgetUsed > availableBudget ? totalBudgetUsed - availableBudget : 0;

  return {
    isValid: totalBudgetUsed <= availableBudget,
    availableBudget,
    totalBudgetUsed,
    overdraft
  };
}

/**
 * Format budget display string
 */
export function formatBudget(amount: number, currency: string = 'EUR'): string {
  return `${amount.toFixed(2)} ${currency}`;
}
